import React, { useState, useEffect, useCallback, useContext } from 'react';
import api from '../utils/api';
import { useHistory, Link } from 'react-router-dom';
import { DepositContext } from '../context/DepositContext';


const DepositTicketForm = () => {
    const today = new Date().toISOString().split('T')[0];
    const history = useHistory(); // Initialize history
    const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 6))
        .toISOString()
        .split('T')[0];

    const { depositData, setDepositData } = useContext(DepositContext);

    const [formData, setFormData] = useState({
        deptkt_date: today,
        deno_100: 0,
        deno_50: 0,
        deno_20: 0,
        deno_10: 0,
        deno_5: 0,
        deno_2: 0,
        deno_1: 0,
        start_range: depositData?.checkStart || sevenDaysAgo,
        end_range: depositData?.checkEnd || today,
    });

    const [checks, setChecks] = useState([]);
    const [totalChecksAmt, setChecksTotal] = useState(0); // Initialize as a number
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const denominations = {
        deno_100: 100,
        deno_50: 50,
        deno_20: 20,
        deno_10: 10,
        deno_5: 5,
        deno_2: 2,
        deno_1: 1,
    };

    const calculateTotal = () => {
        const total = Object.keys(denominations).reduce((acc, key) => {
            const count = parseInt(formData[key], 10) || 0;
            return acc + count * denominations[key];
        }, 0);
        return total.toFixed(2);
    };

    const formatDate = (dateStr) => {
        // Check if dateStr is undefined, null, or an empty string
        if (!dateStr) {
            return ''; // Return an empty string or any default value if dateStr is not valid
        }

        // Split the date string into its components
        const [year, month, day] = dateStr.split('-');

        // Return the formatted date as MM/DD/YYYY
        return `${month}/${day}/${year}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Fetch the checks based on the date range
    const fetchChecks = useCallback(async () => {
        setLoading(true);
        try {
            const { start_range, end_range } = formData;

            // Date range validation
            if (!start_range || !end_range) {
                setErrorMessage('Both Start Date and End Date are required.');
                setLoading(false);
                return;
            }

            if (new Date(end_range) < new Date(start_range)) {
                setErrorMessage('End Date cannot be before Start Date.');
                setLoading(false);
                return;
            }

            const response = await api.get('/api/checkreceipts', {
                params: {
                    startDate: start_range,
                    endDate: end_range,
                },
            });

            setChecks(response.data.data || []);
            setChecksTotal(response.data.totalDonation || 0.0); // Set the total_donations field
            setErrorMessage(''); // Clear any previous errors
        } catch (error) {
            setErrorMessage(
                error.response?.data?.error || 'Failed to fetch checks. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    }, [formData]); // Include formData in dependencies to ensure latest formData values are used

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const response = await api.post('/api/depositTickets', formData);

            // Extract the id from the response
            const { id } = response.data;

            setSuccessMessage(`Deposit ticket created successfully! ID: ${id}`);
            // Navigate to the view screen for the created deposit ticket
            history.push(`/viewDepositTicket/${id}?pagetkt=1`);

        } catch (error) {
            setErrorMessage(
                error.response?.data?.error || 'Failed to add deposit ticket'
            );
        } finally {
            setLoading(false);
        }
    };

    const currTotal = calculateTotal();

    const grandTotal = ((Number(currTotal) || 0) + (Number(totalChecksAmt) || 0)).toFixed(2);

    // Trigger fetching checks when the date range changes
    useEffect(() => {
        fetchChecks();
    }, [fetchChecks]); // Dependency on fetchChecks to avoid missing dependencies warning

    const handleReceiptClick = () => {        
        setDepositData({
            currentPage: 1,
            checkStart: formData.start_range,
            checkEnd: formData.end_range,
        });
    }
    return (
        <div className="container mt-5">
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <h3 className="mb-0">Deposit Ticket</h3>
                </div>
                <div className="card-body">
                    {successMessage && (
                        <div className="alert alert-success">{successMessage}</div>
                    )}
                    {errorMessage && (
                        <div className="alert alert-danger">{errorMessage}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label htmlFor="start_range" className="form-label">
                                    Start Range:
                                </label>
                                <input
                                    type="date"
                                    id="start_range"
                                    name="start_range"
                                    className="form-control"
                                    value={formData.start_range}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="end_range" className="form-label">
                                    End Range:
                                </label>
                                <input
                                    type="date"
                                    id="end_range"
                                    name="end_range"
                                    className="form-control"
                                    value={formData.end_range}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <h5>Range: {formatDate(formData.start_range)} TO {formatDate(formData.end_range)}</h5>
                        <h4 className="mt-4 mb-3">Checks</h4>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th scope="col">Check Number</th>
                                    <th scope="col">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checks.length > 0 ? (
                                    checks.map((check, index) => (
                                        <tr key={index}>
                                            <td className="align-middle">
                                                <Link
                                                    to={`/receiptDetails/${check.receiptNum}?pagetkt=1`} 
                                                    onClick={() => handleReceiptClick()}
                                                    className="receipt-link"
                                                >
                                                     {check.paymentReference}
                                                </Link>
                                            </td>     
                                            <td>{check.donationAmt}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center">
                                            No checks found for this range
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className="row mt-3 mb-4"> {/* Add bottom margin to create separation */}
                            <div className="col-md-8">
                                <h5>Checks Total:</h5>
                            </div>
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control text-success fw-bold"
                                    readOnly
                                    value={totalChecksAmt}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        {/* Currency Denominations Section */}
                        <h4 className="mt-4 mb-3">Currency Count</h4>
                        {Object.keys(denominations).map((key) => (
                            <div className="row mb-3" key={key}>
                                <div className="col-md-4">
                                    <label htmlFor={key} className="form-label">
                                        {denominations[key]}  x
                                    </label>
                                </div>
                                <div className="col-md-4">
                                    <input
                                        type="number"
                                        id={key}
                                        name={key}
                                        className="form-control"
                                        value={formData[key]}
                                        onChange={handleChange}
                                        min="0"
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <input
                                        type="text"
                                        className="form-control"
                                        readOnly
                                        value={`${(parseInt(formData[key]) || 0) *
                                            denominations[key]
                                            }`}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                        ))}

                        <div className="row mt-4">
                            <div className="col-md-8">
                                <h5>Currency Total:</h5>
                            </div>
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control text-success fw-bold"
                                    readOnly
                                    value={currTotal}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        {/* Grand Total Section */}
                        <div className="row mt-4 mb-4"> {/* Add bottom margin for separation */}
                            <div className="col-md-8">
                                <h5>Total:</h5>
                            </div>
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control text-success fw-bold"
                                    readOnly
                                    value={grandTotal}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="d-grid mt-4">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Add Deposit Ticket'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DepositTicketForm;